import { z } from 'zod'

import { blankToNull, knownWord, lenientList, moneySchema } from '@/schemas/common'

/**
 * Baskets: lists of companies, each with its own columns of figures and a few optional checks that
 * explain who is in or out (`/api/v5/baskets/`). An investor basket's companies are picked by
 * staff; a trader basket's are rebuilt by a fixed rule after every trading day's close.
 *
 * These schemas read the WIRE shape and hand back the app's, as `schemas/signals.ts` does, so
 * `lenientList` can drop one malformed member or verdict on its own: a row a mapper had to touch
 * first would throw before the list ever saw it. Every figure is a decimal string, and `null` is
 * "not recorded", never 0.
 */

/** Who a basket is for, which is also how its companies are picked: by staff, or by a rule. */
export const AUDIENCES = ['investor', 'trader'] as const
export type Audience = (typeof AUDIENCES)[number]

/** A tag ("Quality", "High ROE"). Its slug is what `?tag=` takes. */
export const basketTagSchema = z.object({
  slug: z.string().min(1),
  name: z.string().min(1),
})
export type BasketTag = z.infer<typeof basketTagSchema>

/** In mm-be's display order; may be empty. One bad tag drops alone, never its basket. */
const tagsSchema = lenientList(basketTagSchema, 'basket tag')

/** Login only, no plan gate (mm-be `api/v5/baskets/views.py`). The order is staff's display order. */
export const basketSchema = z
  .object({
    slug: z.string().min(1),
    name: z.string().min(1),
    tagline: z.string().nullable(),
    /** Already absolute (mm-be's `media_url()`). */
    icon_url: z.string().nullable(),
    is_new: z.boolean(),
    /** 0 is real: a basket whose every member has been taken out still lists. */
    member_count: z.number().int().nonnegative(),
    tags: tagsSchema,
  })
  .transform((wire) => ({
    slug: wire.slug,
    name: wire.name,
    tagline: blankToNull(wire.tagline),
    iconUrl: blankToNull(wire.icon_url),
    isNew: wire.is_new,
    memberCount: wire.member_count,
    tags: wire.tags,
  }))
export type Basket = z.infer<typeof basketSchema>

/**
 * Every tag the listed baskets carry, for the filter chips. mm-be sends it in `meta` beside
 * `count`, computed before `?tag=` narrows the list so the chips stay put when one is picked. The
 * spec declares `count` only, so anything missing or malformed reads as no chips.
 */
export const basketsMetaTagsSchema = z
  .object({ tags: tagsSchema })
  .transform((meta) => meta.tags)
  .catch([])

export interface BasketList {
  baskets: Basket[]
  tags: BasketTag[]
}

/**
 * A trader basket's rule. `key` stays open (mm-be adds rules); `description` is the plain-words
 * "How it's picked".
 */
const basketRuleSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  description: z.string().min(1),
})
export type BasketRule = z.infer<typeof basketRuleSchema>

/** Where a column's figure comes from, which its head explains. */
export const FIGURE_SOURCES = ['market', 'line_item', 'metric'] as const
export type FigureSource = (typeof FIGURE_SOURCES)[number]

/**
 * One column, or the figure a check tests. `label` and `unit` are the source's own: mm-be never
 * renames a figure, and a code its source no longer defines comes back with the code as its label
 * (`known: false`), which is shown as it is. `unit` stays open (CURRENCY, PERCENT, RATIO,
 * PER_SHARE, COUNT, DAYS, or null).
 */
export const figureSchema = z
  .object({
    key: z.string().min(1),
    source: z.string(),
    label: z.string().min(1),
    unit: z.string().nullable(),
  })
  .transform((wire) => ({
    key: wire.key,
    source: knownWord(FIGURE_SOURCES, wire.source),
    label: wire.label,
    unit: wire.unit,
  }))
export type Figure = z.infer<typeof figureSchema>

/**
 * One basket member. `cells[i]` is the figure for `columns[i]`: the pairing is positional, so a
 * malformed cell fails its whole member rather than shifting every figure after it one column
 * left. The filing period each cell carries is left unread (owner Q6: not in v1).
 */
const memberSchema = z
  .object({
    symbol: z.string().min(1),
    name: z.string(),
    sector: z.string().nullable(),
    cells: z.array(z.object({ value: moneySchema.nullable() })),
  })
  .transform((wire) => ({
    symbol: wire.symbol,
    name: wire.name,
    sector: blankToNull(wire.sector),
    values: wire.cells.map((cell) => cell.value),
  }))
export type BasketMember = z.infer<typeof memberSchema>

export const basketDetailSchema = z
  .object({
    slug: z.string().min(1),
    name: z.string().min(1),
    tagline: z.string().nullable(),
    icon_url: z.string().nullable(),
    /** Unknown to this build reads as `null`: the page then links back to the default list. */
    audience: z.string(),
    tags: tagsSchema,
    /** Cleaned by mm-be (`nh3`): formatting and links only. Sanitised again on render. */
    how_its_built: z.string().nullable(),
    /** Null for an investor basket. A malformed one is dropped, not the basket. */
    rule: basketRuleSchema.nullable().catch(null),
    columns: z.array(figureSchema),
    members: lenientList(memberSchema, 'basket member'),
    figures_as_of: z.string().nullable(),
  })
  .transform((wire) => ({
    slug: wire.slug,
    name: wire.name,
    tagline: blankToNull(wire.tagline),
    iconUrl: blankToNull(wire.icon_url),
    audience: knownWord(AUDIENCES, wire.audience),
    tags: wire.tags,
    howItsBuilt: blankToNull(wire.how_its_built),
    rule: wire.rule,
    columns: wire.columns,
    // A member whose cells do not line up with the columns would print figures under the wrong
    // heads; it goes, as a malformed row does.
    members: wire.members.filter((member) => member.values.length === wire.columns.length),
    figuresAsOf: wire.figures_as_of,
  }))
export type BasketDetail = z.infer<typeof basketDetailSchema>

/**
 * One stay of one company in a basket (`/history/`): when it came in and, once it left, when and
 * why. `leftOn: null` is still in. The notes are mm-be's own words ("Matched the rule").
 */
export const basketStaySchema = z
  .object({
    symbol: z.string().min(1),
    name: z.string(),
    added_on: z.string().min(1),
    removed_on: z.string().nullable(),
    added_note: z.string().nullable(),
    removed_note: z.string().nullable(),
  })
  .transform((wire) => ({
    symbol: wire.symbol,
    name: wire.name,
    joinedOn: wire.added_on,
    leftOn: wire.removed_on,
    joinedNote: blankToNull(wire.added_note),
    leftNote: blankToNull(wire.removed_note),
  }))
export type BasketStay = z.infer<typeof basketStaySchema>

/**
 * A basket's Past results (`/backtest/`, mm-be #375): Rs 1 lakh in the basket against the same in
 * NEPSE, one point per month-end, built every evening. Money split equally, split again each
 * quarter, charges left out. `rule_replay` runs a trader basket's rule on each rebalance day's
 * figures; `current_members` holds today's members throughout, which flatters the basket (the
 * screen says so). An empty `series` (too little history, or not built yet) hides the tab.
 */
export const BACKTEST_METHODS = ['rule_replay', 'current_members'] as const
export type BacktestMethod = (typeof BACKTEST_METHODS)[number]

export const basketBacktestSchema = z
  .object({
    method: z.enum(BACKTEST_METHODS).nullable().catch(null),
    start: z.string().nullable(),
    end: z.string().nullable(),
    as_of: z.string().nullable(),
    series: z.array(z.object({ date: z.string().min(1), basket: moneySchema, nepse: moneySchema })),
    cagr: z.object({ basket: moneySchema.nullable(), nepse: moneySchema.nullable() }),
    max_drawdown: moneySchema.nullable(),
    months_ahead_pct: moneySchema.nullable(),
    years: z.array(
      z.object({
        year: z.number().int(),
        basket: moneySchema.nullable(),
        nepse: moneySchema.nullable(),
        partial: z.boolean(),
      }),
    ),
  })
  .transform((wire) => ({
    method: wire.method,
    start: wire.start,
    end: wire.end,
    asOf: wire.as_of,
    series: wire.series,
    cagr: wire.cagr,
    maxDrawdown: wire.max_drawdown,
    monthsAheadPct: wire.months_ahead_pct,
    years: wire.years,
  }))
export type BasketBacktest = z.infer<typeof basketBacktestSchema>

export const CHECK_OPERATORS = ['gte', 'gt', 'lte', 'lt'] as const
export type CheckOperator = (typeof CHECK_OPERATORS)[number]

/** One check run on one company. `passed: null` is a figure not recorded, which cannot pass. */
const checkSchema = z
  .object({
    figure: figureSchema,
    operator: z.string(),
    threshold: moneySchema.nullable(),
    value: moneySchema.nullable(),
    passed: z.boolean().nullable(),
  })
  .transform((wire) => ({
    figure: wire.figure,
    operator: knownWord(CHECK_OPERATORS, wire.operator),
    threshold: wire.threshold,
    value: wire.value,
    passed: wire.passed,
  }))
export type BasketCheck = z.infer<typeof checkSchema>

/**
 * in = a member; near = passes every check but not picked (only a basket with checks has these);
 * out = fails one; not_checked = a listed stock outside the basket's group (the one-company check
 * only). A status this build does not know drops the row: it decides which list the row is in.
 */
export const VERDICT_STATUSES = ['in', 'near', 'out', 'not_checked'] as const
export type VerdictStatus = (typeof VERDICT_STATUSES)[number]

export const verdictSchema = z.object({
  symbol: z.string().min(1),
  name: z.string(),
  status: z.enum(VERDICT_STATUSES),
  checks: z.array(checkSchema),
})
export type Verdict = z.infer<typeof verdictSchema>

/**
 * The funnel over the In or Out list. mm-be sends these in `meta` beside the envelope's `count`
 * (`VerdictMetaSerializer`), but the published spec declares `count` only, so they are read
 * leniently: anything missing or malformed is `null` and the screen works the figures out from
 * the rows instead. `passedEveryCheck` is null for a basket with no checks.
 */
const inOutMetaSchema = z
  .object({
    checked: z.number().int().nonnegative(),
    passed_every_check: z.number().int().nonnegative().nullable(),
    in_basket: z.number().int().nonnegative(),
  })
  .transform((wire) => ({
    checked: wire.checked,
    passedEveryCheck: wire.passed_every_check,
    inBasket: wire.in_basket,
  }))
export type InOutFunnel = z.infer<typeof inOutMetaSchema>

export const inOutMetaOrNullSchema = inOutMetaSchema.nullable().catch(null)

export interface BasketInOut {
  /** Members in staff's order, then the near misses, then the rest, each by symbol. */
  verdicts: Verdict[]
  funnel: InOutFunnel | null
}
